// _namespaces.js MUST come before any model/view/collection import — it
// bootstraps the AppointmentBooking.SlotSuggester class and its static
// namespace properties (Views, Models, Collections, Events) so that
// sub-modules can reference them at module-evaluation time.
import "./_namespaces.js";
import "#/shared/api/api-host-global.js";

import "../../utilities/caseworker.js";
import "../../utilities/params.js";
import.meta.glob("./models/*.{js,hbs,jst.ejs}", { eager: true });
import.meta.glob("./collections/*.{js,hbs,jst.ejs}", { eager: true });
import.meta.glob("./views/*.{js,hbs,jst.ejs}", { eager: true });
import.meta.glob("./templates/**/*.{js,hbs,jst.ejs}", { eager: true });
import "../../../../../lib/assets/javascripts/assert.js";
import "virtual:client-templates/app/assets/javascripts/appointment_booking/slot_suggester/templates";

// The SlotSuggester class shell and its static namespace properties (Views,
// Models, Collections, Events) are created in _namespaces.js. Here we attach
// the real constructor logic (_init) and instance methods to the prototype.

const SlotSuggester = AppointmentBooking.SlotSuggester;

SlotSuggester.prototype.apiUrl = window.Sparkle.apiHost
  ? window.Sparkle.apiHost + window.SparkleConfig?.routes?.embed?.ehrAppointmentSlotsSlotSuggester
  : undefined;

SlotSuggester.get("matchingSlots", function () {
  return this.model.get("matchingSlots");
});

SlotSuggester.prototype._init = function (options) {
  // Make it easy for views to assert things
  this.render = this.render.bind(this);
  this.setSelectedSlotId = this.setSelectedSlotId.bind(this);
  this.setDesiredSlotConditions = this.setDesiredSlotConditions.bind(this);
  this.fetchReselectionData = this.fetchReselectionData.bind(this);
  this.processReselectionData = this.processReselectionData.bind(this);
  this.assignProviderToSlot = this.assignProviderToSlot.bind(this);
  this.withLoadingIndicator = this.withLoadingIndicator.bind(this);
  this.triggerEvent = this.triggerEvent.bind(this);
  if (options == null) {
    options = {};
  }
  this.assert = new Asserter({ notifyUser: false, reportData: { options } });

  this.model = new this.constructor.Models.Reselector({
    variant: options.variant,
    showMessage: options.showMessage,
    desiredSlotConditions: options.desiredSlotConditions || {},
  });

  this.view = new this.constructor.Views.Reselector({ model: this.model });

  this.el = this.view.el;

  // INITIALIZE

  this.fetchReselectionData();
};

SlotSuggester.prototype.render = function () {
  return this.view.render();
};

// API

SlotSuggester.prototype.setSelectedSlotId = function (slotId) {
  return this.model.set("selectedSlotId", slotId);
};

SlotSuggester.prototype.setDesiredSlotConditions = function (desiredSlotConditions) {
  this.model.set({ desiredSlotConditions });
  return this.fetchReselectionData();
};

// HELPER METHODS

SlotSuggester.prototype.fetchReselectionData = function () {
  const deferred = new jQuery.Deferred();
  const desiredSlotConditions = Sparkle.Utilities.sanitizeParams(this.model.get("desiredSlotConditions"));

  if (_.isEmpty(desiredSlotConditions)) {
    deferred.resolve(this);
  } else {
    this.withLoadingIndicator(() =>
      jQuery
        .get(
          this.apiUrl,
          { desired_slot_conditions: Sparkle.Utilities.camelToSnakeObject(desiredSlotConditions) },
          this.processReselectionData,
        )
        .then(() => deferred.resolve(this)),
    );
  }

  return deferred.promise();
};

SlotSuggester.prototype.processReselectionData = function (response) {
  // Normalize
  let desiredProvider;

  let selectedDateStamp;
  response = Sparkle.Utilities.snakeToCamelObject(response);

  // Instantiate

  const availableProviders = new this.constructor.Collections.Providers(response.referencedProviders);
  const matchingSlots = new this.constructor.Collections.Slots(response.matchingSlots || []);
  const suggestedSlots = new this.constructor.Collections.Slots(response.suggestedSlots || []);
  const { reselectionReason } = response;
  const { suggestionAlgorithmKey } = response;
  const { suggestionAlgorithmTitle } = response;
  const { suggestionDateBias } = response;

  // Match up providers to the slots. We have all the providers, but not *every* possible slot, so these must be separate lists or we would be missing providers.
  matchingSlots.forEach((slot) => this.assignProviderToSlot(slot, availableProviders));
  suggestedSlots.forEach((slot) => this.assignProviderToSlot(slot, availableProviders));

  const desiredStartTime = response.desired.startTime;
  const desiredDepartmentId = response.desired.departmentId;
  if (response.desired.provider != null) {
    desiredProvider = availableProviders.findWhere(response.desired.provider);
  }
  const desiredProviderSlots = new this.constructor.Collections.Slots(
    suggestedSlots.where({ providerId: desiredProvider.get("id"), providerType: desiredProvider.get("type") }),
  );
  const poolSlots = new this.constructor.Collections.Slots(
    suggestedSlots.reject({ providerId: desiredProvider.get("id"), providerType: desiredProvider.get("type") }),
  );
  const poolProviders = new this.constructor.Collections.Providers(
    availableProviders.reject({ gid: desiredProvider.get("gid") }),
  );
  const bookingPhoneNumber = response.bookingNumber;

  const attrs = {
    desiredStartTime,
    desiredDepartmentId,
    desiredProvider,
    matchingSlots,
    availableProviders,
    suggestedSlots,
    desiredProviderSlots,
    poolSlots,
    poolProviders,
    reselectionReason,
    suggestionAlgorithmKey,
    suggestionAlgorithmTitle,
    suggestionDateBias,
    bookingPhoneNumber,
  };

  // Initialize the selected provider option based on whether we have a desired slot or suggested slots
  if (desiredProviderSlots.length) {
    attrs.selectedProviderOption = "desired";
  } else if (poolSlots.length) {
    attrs.selectedProviderOption = "pool";
  }

  // Initialize the selected date option based on whether we have a desired slot or suggested slots
  if (desiredProviderSlots.length) {
    selectedDateStamp = desiredProviderSlots.first().startMoment;
  } else if (poolSlots.length) {
    selectedDateStamp = poolSlots.first().startMoment;
  } else if (desiredStartTime) {
    selectedDateStamp = desiredStartTime;
  } else {
    selectedDateStamp = moment();
  }

  attrs.selectedDate = moment.parseZone(selectedDateStamp); // Make sure we keep the time zone so this is not parsed into the local timezone when used

  this.model.set(attrs);
  return this.model.trigger("reset"); // Notify listeners that a full reset has occurred so they don't need to listen for events that trigger only on data fetch
};

SlotSuggester.prototype.assignProviderToSlot = function (slot, availableProviders) {
  const provider = this.assert.presence(
    availableProviders.findWhere({ id: slot.get("providerId"), type: slot.get("providerType") }),
    `Can't find provider for slot ${slot.id}`,
  );
  return slot.set({ provider });
};

// Executes the function which returns a promise, and shows a loading indicator until that promise resolves.
SlotSuggester.prototype.withLoadingIndicator = function (promiseFunction) {
  this.triggerEvent("loading.sparkle");
  const promise = promiseFunction();
  promise.always(() => this.triggerEvent("loaded.sparkle"));

  return promise;
};

// Used to communicate UI interaction events up to parent views
SlotSuggester.prototype.triggerEvent = function (event, ...payload) {
  return jQuery(this.el).trigger(event, payload);
};
